import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../contexts/LanguageContext";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Parse simple markdown (italics and bold) to JSX
 * Converts *text* to <em>text</em> and **text** to <strong>text</strong>
 */
const parseMarkdown = (text) => {
    if (!text || typeof text !== 'string') return text;
    
    // Split by markdown patterns while preserving them
    const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
    
    return parts.map((part, i) => {
        // Bold: **text**
        if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={i} className="font-semibold text-primary">{part.slice(2, -2)}</strong>;
        }
        // Italic: *text*
        if (part.startsWith('*') && part.endsWith('*')) {
            return <em key={i} className="italic text-primary/80">{part.slice(1, -1)}</em>;
        }
        return part;
    });
};

/**
 * Tone-based styling configuration
 */
const TONE_STYLES = {
    roast: {
        borderColor: "rgba(244, 67, 54, 0.4)",
        glowColor: "rgba(244, 67, 54, 0.2)",
        accentColor: "#F44336",
        emoji: "🔥",
        label: { en: "REALITY CHECK", es: "CHEQUEO DE REALIDAD" }
    },
    reality_check: {
        borderColor: "rgba(255, 152, 0, 0.4)",
        glowColor: "rgba(255, 152, 0, 0.2)",
        accentColor: "#FF9800",
        emoji: "⚡",
        label: { en: "WAKE UP CALL", es: "LLAMADA DE ATENCIÓN" }
    },
    balanced: {
        borderColor: "rgba(212, 175, 55, 0.4)",
        glowColor: "rgba(212, 175, 55, 0.2)",
        accentColor: "#D4AF37",
        emoji: "⚖️",
        label: { en: "COSMIC TRUTH", es: "VERDAD CÓSMICA" }
    },
    encouragement: {
        borderColor: "rgba(76, 175, 80, 0.4)",
        glowColor: "rgba(76, 175, 80, 0.2)",
        accentColor: "#4CAF50",
        emoji: "✨",
        label: { en: "RISING ENERGY", es: "ENERGÍA EN ASCENSO" }
    },
    hype: {
        borderColor: "rgba(156, 39, 176, 0.4)",
        glowColor: "rgba(156, 39, 176, 0.3)",
        accentColor: "#9C27B0",
        emoji: "🚀",
        label: { en: "COSMIC PEAK", es: "PICO CÓSMICO" }
    }
};

/**
 * Category icons and colors
 */
const CATEGORY_CONFIG = {
    love: { icon: "♥", color: "#E91E63", planet: "Venus" },
    career: { icon: "⚡", color: "#4CAF50", planet: "Jupiter" },
    health: { icon: "✧", color: "#2196F3", planet: "Mars" },
    spirit: { icon: "☆", color: "#9C27B0", planet: "Neptune" }
};

/**
 * Planet translations for all supported languages
 */
const PLANET_TRANSLATIONS = {
    "Sun": { es: "Sol", en: "Sun" },
    "Moon": { es: "Luna", en: "Moon" },
    "Mercury": { es: "Mercurio", en: "Mercury" },
    "Venus": { es: "Venus", en: "Venus" },
    "Mars": { es: "Marte", en: "Mars" },
    "Jupiter": { es: "Júpiter", en: "Jupiter" },
    "Saturn": { es: "Saturno", en: "Saturn" },
    "Uranus": { es: "Urano", en: "Uranus" },
    "Neptune": { es: "Neptuno", en: "Neptune" },
    "Pluto": { es: "Plutón", en: "Pluto" }
};

// Zodiac sign translations
const SIGN_TRANSLATIONS = {
    "Aries": "Aries", "Taurus": "Tauro", "Gemini": "Géminis", "Cancer": "Cáncer",
    "Leo": "Leo", "Virgo": "Virgo", "Libra": "Libra", "Scorpio": "Escorpio",
    "Sagittarius": "Sagitario", "Capricorn": "Capricornio", "Aquarius": "Acuario", "Pisces": "Piscis"
};

// Moon phase translations
const MOON_PHASE_TRANSLATIONS = {
    "New Moon": "Luna Nueva", "Waxing Crescent": "Luna Creciente", "First Quarter": "Cuarto Creciente",
    "Waxing Gibbous": "Gibosa Creciente", "Full Moon": "Luna Llena", "Waning Gibbous": "Gibosa Menguante",
    "Last Quarter": "Cuarto Menguante", "Waning Crescent": "Luna Menguante"
};

const getTranslatedSign = (sign, lang) => lang === 'es' ? (SIGN_TRANSLATIONS[sign] || sign) : sign;
const getTranslatedPhase = (phase, lang) => lang === 'es' ? (MOON_PHASE_TRANSLATIONS[phase] || phase) : phase;

/**
 * StatusInsightModal - Shows Ruthless Cosmic Coach roast/hype for energy categories
 * Now with streaming typewriter effect
 */
const StatusInsightModal = ({ isOpen, onClose, category, displayName, score, reason }) => {
    const { language, t } = useLanguage();
    const [streamingText, setStreamingText] = useState("");
    const [metadata, setMetadata] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0); // Trigger for retry
    const abortControllerRef = useRef(null);
    const contentRef = useRef(null);
    
    const categoryConfig = CATEGORY_CONFIG[category?.toLowerCase()] || CATEGORY_CONFIG.spirit;
    
    // Use displayName for UI, category (internal key) for API
    const categoryDisplayName = displayName || category?.toUpperCase() || "";
    
    // Fetch streaming insight - memoized
    const fetchStreamingInsight = useCallback(async () => {
        if (!category || score === undefined) return;
        
        // Cancel any existing request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        
        abortControllerRef.current = new AbortController();
        
        setLoading(true);
        setIsStreaming(false);
        setStreamingText("");
        setMetadata(null);
        setError(null);
        
        // Get user name from onboarding data (same logic as OracleSection/Houses)
        let userName = '';
        try {
            const onboardingData = localStorage.getItem('astral_belonging_onboarding');
            if (onboardingData) {
                const parsed = JSON.parse(onboardingData);
                userName = parsed?.userData?.name || '';
            }
        } catch (e) {
            console.warn('Could not parse onboarding data for user name');
        }
        
        try {
            // Build URL with user_name parameter
            let url = `${API_URL}/api/energy/insight/stream?lang=${language}`;
            if (userName) {
                url += `&user_name=${encodeURIComponent(userName)}`;
            }
            
            const response = await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                    category: category.toLowerCase(),
                    score: score,
                    reason: reason || "No additional context"
                }),
                signal: abortControllerRef.current.signal
            });
            
            if (!response.ok) {
                throw new Error(language === 'es' ? "Hubo un corto circuito cósmico..." : "The Oracle is meditating...");
            }
            
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            
            // Add a small delay so "Oracle is channeling" message shows briefly
            await new Promise(resolve => setTimeout(resolve, 800));
            
            setLoading(false);
            setIsStreaming(true);
            
            let buffer = "";
            
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || "";
                
                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.slice(6));
                            
                            if (data.type === 'metadata') {
                                setMetadata(data);
                            } else if (data.type === 'text') {
                                setStreamingText(prev => prev + data.content);
                            } else if (data.type === 'done') {
                                setIsStreaming(false);
                            }
                        } catch (e) {
                            // Ignore parse errors for incomplete JSON
                        }
                    }
                }
            }
            
            setIsStreaming(false);
            
        } catch (err) {
            if (err.name === 'AbortError') return;
            console.error("Streaming insight error:", err);
            setError(err.message || (language === 'es' ? "Hubo un corto circuito cósmico..." : "Connection error"));
            setLoading(false);
            setIsStreaming(false);
        }
    }, [category, score, reason, language]);
    
    // Handle retry - simply increment counter to trigger useEffect
    const handleRetry = useCallback(() => {
        console.log("REINTENTAR clicked - forcing refetch");
        setRetryCount(prev => prev + 1);
    }, []);
    
    // Start streaming when modal opens OR when retry is triggered
    useEffect(() => {
        if (isOpen) {
            fetchStreamingInsight();
        }
        
        // Cleanup on unmount
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [isOpen, retryCount, fetchStreamingInsight]);
    
    // Reset when modal closes
    const handleClose = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        setStreamingText("");
        setMetadata(null);
        setError(null);
        setIsStreaming(false);
        onClose();
    };
    
    const toneStyle = metadata ? TONE_STYLES[metadata.tone] || TONE_STYLES.balanced : TONE_STYLES.balanced;
    
    // Loading text based on language
    const loadingText = language === 'es' 
        ? "El Oráculo está canalizando tu energía..." 
        : "The Oracle is channeling your energy...";
    
    const tryAgainText = language === 'es' ? "Reintentar" : "Try Again";
    
    return (
        <AnimatePresence>
            {isOpen && (
                <span>
                    {/* Backdrop */}
                    <motion.div
                        className="fixed inset-0 bg-black/70 backdrop-blur-md z-50"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={handleClose}
                    />
                    
                    {/* Modal */}
                    <motion.div
                        className="fixed inset-0 z-50 flex items-center justify-center p-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            className="w-full max-w-md rounded-2xl overflow-hidden"
                            style={{
                                background: "linear-gradient(135deg, rgba(20,18,28,0.98) 0%, rgba(12,10,18,0.99) 100%)",
                                border: `1px solid ${metadata ? toneStyle.borderColor : 'rgba(212, 175, 55, 0.2)'}`,
                                boxShadow: metadata ? `0 0 40px ${toneStyle.glowColor}, inset 0 0 60px rgba(0,0,0,0.5)` : 'none'
                            }}
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="p-4 border-b border-white/5">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        {/* Category Icon */}
                                        <div 
                                            className="w-10 h-10 rounded-full flex items-center justify-center text-xl"
                                            style={{ 
                                                background: `${categoryConfig.color}20`,
                                                border: `2px solid ${categoryConfig.color}40`
                                            }}
                                        >
                                            {categoryConfig.icon}
                                        </div>
                                        <div>
                                            <h3 className="font-cinzel text-lg uppercase" style={{ color: categoryConfig.color }}>
                                                {categoryDisplayName}
                                            </h3>
                                            <p className="text-xs text-foreground/50">
                                                {score}% • {PLANET_TRANSLATIONS[categoryConfig.planet]?.[language] || categoryConfig.planet} {language === 'es' ? 'Influencia' : 'Influence'}
                                            </p>
                                        </div>
                                    </div>
                                    
                                    {/* Close Button */}
                                    <button
                                        onClick={handleClose}
                                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/5 transition-colors"
                                        data-testid="insight-modal-close"
                                    >
                                        <span className="text-foreground/50">✕</span>
                                    </button>
                                </div>
                                
                                {/* Tone Badge */}
                                {metadata && (
                                    <motion.div 
                                        className="mt-3 flex items-center gap-2"
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                    >
                                        <span className="text-lg">{toneStyle.emoji}</span>
                                        <span 
                                            className="text-[10px] font-inter uppercase tracking-[0.2em] px-2 py-1 rounded"
                                            style={{ 
                                                background: `${toneStyle.accentColor}20`,
                                                color: toneStyle.accentColor,
                                                border: `1px solid ${toneStyle.accentColor}40`
                                            }}
                                        >
                                            {toneStyle.label[language] || toneStyle.label.en}
                                        </span>
                                    </motion.div>
                                )}
                            </div>
                            
                            {/* Content */}
                            <div className="p-5">
                                {(loading || (isStreaming && !streamingText)) ? (
                                    <div className="flex flex-col items-center justify-center py-8">
                                        <motion.div
                                            className="w-8 h-8 rounded-full border-2 border-primary/30"
                                            style={{ borderTopColor: categoryConfig.color }}
                                            animate={{ rotate: 360 }}
                                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                        />
                                        <p className="mt-4 text-sm text-foreground/50 italic">
                                            {loadingText}
                                        </p>
                                    </div>
                                ) : error ? (
                                    <div className="text-center py-8">
                                        <span className="text-2xl">💫</span>
                                        <p className="mt-3 text-sm text-foreground/60">
                                            {language === 'es' 
                                                ? "Hubo un corto circuito cósmico... vamos de nuevo" 
                                                : "A cosmic short circuit... let's try again"}
                                        </p>
                                        <button
                                            onClick={handleRetry}
                                            className="mt-4 px-4 py-2 text-xs uppercase tracking-wider rounded hover:scale-105 transition-transform"
                                            style={{ 
                                                background: 'rgba(212, 175, 55, 0.1)',
                                                border: '1px solid rgba(212, 175, 55, 0.3)',
                                                color: '#D4AF37'
                                            }}
                                            data-testid="insight-retry-btn"
                                        >
                                            {tryAgainText}
                                        </button>
                                    </div>
                                ) : (streamingText || isStreaming) ? (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.1 }}
                                    >
                                        {/* The Insight - Streaming typewriter effect */}
                                        <div 
                                            ref={contentRef}
                                            className="p-4 rounded-lg min-h-[80px]"
                                            style={{ background: "rgba(0,0,0,0.3)" }}
                                        >
                                            <p className="text-sm font-inter text-foreground/90 leading-relaxed">
                                                {parseMarkdown(streamingText)}
                                                {/* Blinking cursor while streaming */}
                                                {isStreaming && (
                                                    <motion.span
                                                        className="inline-block w-0.5 h-4 ml-0.5 align-middle"
                                                        style={{ background: toneStyle.accentColor }}
                                                        animate={{ opacity: [1, 0, 1] }}
                                                        transition={{ duration: 0.8, repeat: Infinity }}
                                                    />
                                                )}
                                            </p>
                                        </div>
                                        
                                        {/* Cosmic Context - Show after streaming completes */}
                                        {!isStreaming && metadata?.cosmic_context && (
                                            <motion.div 
                                                className="mt-4 flex flex-wrap gap-2 justify-center"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{ delay: 0.3 }}
                                            >
                                                {metadata.cosmic_context?.moon_sign && (
                                                    <span className="px-2 py-1 rounded text-[9px] font-inter bg-primary/10 text-primary/60 border border-primary/20">
                                                        ☽ {getTranslatedSign(metadata.cosmic_context.moon_sign, language)}
                                                    </span>
                                                )}
                                                {metadata.cosmic_context?.moon_phase && (
                                                    <span className="px-2 py-1 rounded text-[9px] font-inter bg-primary/10 text-primary/60 border border-primary/20">
                                                        {getTranslatedPhase(metadata.cosmic_context.moon_phase, language)}
                                                    </span>
                                                )}
                                            </motion.div>
                                        )}
                                    </motion.div>
                                ) : null}
                            </div>
                            
                            {/* Score Bar */}
                            <div className="px-5 pb-5">
                                <div className="relative h-2 rounded-full overflow-hidden bg-black/40">
                                    <motion.div
                                        className="absolute left-0 top-0 h-full rounded-full"
                                        style={{ 
                                            background: `linear-gradient(90deg, ${categoryConfig.color}60, ${categoryConfig.color})`,
                                            boxShadow: `0 0 10px ${categoryConfig.color}60`
                                        }}
                                        initial={{ width: 0 }}
                                        animate={{ width: `${score}%` }}
                                        transition={{ duration: 1, delay: 0.3 }}
                                    />
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </span>
            )}
        </AnimatePresence>
    );
};

export default StatusInsightModal;
